import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Check, Lock, ArrowLeft, Loader2, Shield } from 'lucide-react';

const PRODUCTS = {
  foundation: {
    name: "AI Tools for Creatives — Foundation",
    price: 147,
    originalPrice: 297,
    description: "Perfect for solo creators who want to build",
    features: [
      "All 11 video modules (delivered weekly)",
      "Story Framework Formula book",
      "All 10+ templates & prompts",
      "Private community access",
      "Lifetime access to course"
    ]
  },
  pro: {
    name: "AI Tools for Creatives — Pro",
    price: 497,
    description: "For creators who want to sell their IP",
    badge: "MOST POPULAR",
    features: [
      "Everything in Foundation, PLUS:",
      "\"How I Sold 10 IPs\" Bonus Module (4 lessons)",
      "Pitch Deck Review (Ramon's feedback on YOUR deck)",
      "Crowdfunding Campaign Checklist ($1.6M Kickstarter)",
      "The Lion Forge Deal breakdown",
      "What buyers actually want to see"
    ]
  },
  vip: {
    name: "AI Tools for Creatives — VIP",
    price: 1997,
    priceDisplay: "1,997",
    description: "For serious creators who want direct access",
    limited: "Only 10 spots available",
    features: [
      "Everything in Pro, PLUS:",
      "90-Minute 1-on-1 Strategy Call with Ramon",
      "Pitch Practice Session (real-time coaching)",
      "Direct Industry Connections (when applicable)",
      "Lifetime Priority Support",
      "First in line for future updates"
    ]
  }
};

const AIToolsCheckout = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const product = PRODUCTS[productId];
  
  if (!product) {
    return (
      <div className="min-h-screen bg-[#08090d] text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-serif mb-4">Product not found</h1>
          <button
            onClick={() => navigate('/ai-tools-for-creatives')}
            className="text-[#E5C77A] underline"
          >
            Return to course page
          </button>
        </div>
      </div>
    );
  }

  const handleCheckout = async () => {
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkout/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: productId,
          email,
          origin_url: window.location.origin.replace('/checkout/' + productId, ''),
          order_bump: false
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }
      
      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError('Failed to process checkout. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#08090d] text-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <style>{`
        .serif-heading { font-family: 'Playfair Display', Georgia, serif; }
        .premium-button {
          background: linear-gradient(135deg, #C6A85E, #E5C77A);
          box-shadow: 0 8px 32px rgba(198, 168, 94, 0.3);
          transition: all 250ms ease;
        }
        .premium-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 12px 40px rgba(198, 168, 94, 0.4);
        }
        .premium-button:disabled { opacity: 0.7; cursor: not-allowed; }
        .glass-card {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(12px);
        }
      `}</style>

      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-10">
          <button
            onClick={() => navigate('/ai-tools-for-creatives#pricing')}
            className="flex items-center gap-2 text-white/50 hover:text-white transition-colors mb-6"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            Back to pricing
          </button>
          
          {product.badge && (
            <span className="inline-block bg-[#E5C77A] text-[#0F1115] px-3 py-1 rounded-full text-xs font-bold mb-4">
              {product.badge}
            </span>
          )}
          
          <h1 className="serif-heading text-3xl md:text-4xl font-normal mb-2">{product.name}</h1>
          <p className="text-white/60">{product.description}</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Product Info */}
          <div className="lg:col-span-3">
            <div className="glass-card rounded-xl p-8 mb-6">
              <h3 className="text-xl font-semibold mb-6">What's Included:</h3>
              <ul className="space-y-4">
                {product.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check size={18} className="text-[#E5C77A] mt-0.5 shrink-0" />
                    <span className="text-white/80">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap gap-4 text-sm text-white/50">
              <span className="flex items-center gap-2">
                <Shield size={16} className="text-[#E5C77A]" />
                30-Day Money-Back Guarantee
              </span>
              <span className="flex items-center gap-2">
                <Shield size={16} className="text-[#E5C77A]" />
                Lifetime Access
              </span>
              <span className="flex items-center gap-2">
                <Shield size={16} className="text-[#E5C77A]" />
                Secure Checkout
              </span>
            </div>
          </div>

          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <div className="glass-card rounded-xl p-8 sticky top-6">
              <h3 className="text-xl font-semibold mb-6">Complete Your Order</h3>
              
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm text-white/60 mb-2">Full Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="w-full p-4 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-[#E5C77A] transition-colors"
                    data-testid="checkout-name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-white/60 mb-2">Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full p-4 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-[#E5C77A] transition-colors"
                    data-testid="checkout-email"
                  />
                </div>
              </div>

              {/* Order Summary */}
              <div className="border-t border-white/10 pt-6 mb-6">
                <div className="flex justify-between text-white/60 mb-2">
                  <span>{product.name}</span>
                </div>
                {product.originalPrice && (
                  <div className="flex justify-between text-sm text-white/40 mb-2">
                    <span>Launch Discount</span>
                    <span>-${product.originalPrice - product.price}</span>
                  </div>
                )}
                <div className="flex justify-between text-xl font-bold pt-4 border-t border-white/10">
                  <span>Total</span>
                  <div>
                    {product.originalPrice && (
                      <span className="text-white/40 line-through text-base mr-2">${product.originalPrice}</span>
                    )}
                    <span className="text-[#E5C77A]">${product.priceDisplay || product.price}</span>
                  </div>
                </div>
              </div>

              {error && (
                <p className="text-red-400 text-sm mb-4" data-testid="checkout-error">{error}</p>
              )}

              <button
                onClick={handleCheckout}
                disabled={isLoading}
                className="premium-button w-full text-[#0F1115] py-4 font-semibold rounded-lg flex items-center justify-center gap-2"
                data-testid="checkout-submit"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Lock size={18} />
                    Complete Purchase — ${product.priceDisplay || product.price}
                  </>
                )}
              </button>

              <p className="text-xs text-white/40 text-center mt-4">
                Secure checkout powered by Stripe
              </p>
              
              {product.limited && (
                <p className="text-xs text-[#E5C77A] text-center mt-2">
                  {product.limited}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIToolsCheckout;
